// Picking an earlier chat out of the history rail.
//
// The click is one atomic act, and the ORDER of its parts is the whole design:
//
//   guard → refuse-if-the-issue-slot-holds-it → resolve →
//   refuse (revoked, issue-only) → detach the outgoing → adopt → route
//
// Each part answers a way this gesture has gone wrong before. The guard bounds
// the cost (one adoption per gesture, and picking the session already open does
// nothing at all — "already open" being the chat binding only while the SURFACE
// behind the drawer is the chat, which no URL can say: the wizard stages share
// the project URL with the chat while standing in the issue slot). The local
// refusal beside it keeps the two slots disjoint: a session issue work is bound
// to — the pre-warm or any issue's own — cannot also become the chat's, or one
// conversation would answer both.
// The resolve is the only place the two facts that decide the
// landing surface exist — `endedAt` and the derived `flowIssueIid`; neither is
// on the list wire. `flowIssueIid` is also the second refusal: the rail lists
// chats, so a row the server derives an active flow for is wearing a stale
// label, and reopening an issue conversation is withheld here. Refusing a
// revoked session BEFORE any binding write is what
// keeps `useSessionBridgeEnsure` from firing a `POST /reconnect` the server
// answers 409 to and the client swallows into a diagnostic line — a hang with
// no explanation. The detach uses the same `?reason=pagehide` DELETE the tab-
// close beacon does, so the sandbox being left enters the server's detach grace
// instead of being orphaned with its own billed budget window; it must run
// while the outgoing id is still readable, i.e. before the adopt. Every one of
// those reads and writes is the CHAT slot: the rail is a chat surface, so a
// pick moves the chat binding and never an issue's.
//
// Presentation-free on purpose: the rail renders the pending state and the
// typed refusal, and the host decides what a resolved target means for the
// stage. A refusal never resolves a target — nothing navigates, nothing is
// adopted, and the rail is the surface that explains it.

import { useCallback, useRef, useState } from "react";
import { z } from "zod";

import {
  adoptProjectSession,
  isIssueWorkSession,
  readProjectSessionId,
} from "../dashboard/useProjectSession";
import { HttpError, UnauthorizedError, httpGet } from "../lib/http";
import { detachSession } from "../lib/sessionBeacon";
import {
  SessionDetailSchema,
  type SessionIndexEntry,
} from "../projects/useSessions";

/** Where a picked session reopens. */
export type PickTarget =
  /** A refine session — the server derived an active flow for this issue. */
  | { readonly kind: "refine"; readonly iid: number }
  /** No active flow: the session's last user turn was free-form. */
  | { readonly kind: "chat" }
  /**
   * An autonomous implement run. It owns a DEDICATED session, so it is reopened
   * by handing `resumeSessionId` to ImplementFlow — the per-project binding is
   * deliberately left pointing at the PM session.
   */
  | {
      readonly kind: "implement";
      readonly iid: number;
      readonly title: string;
      readonly webUrl: string | null;
      readonly resumeSessionId: string;
    };

/**
 * Why a pick did not land. Five outcomes, because four of them are TERMINAL
 * and reading them all as "try again in a moment" is a lie.
 */
export type PickRefusal =
  /** `endedAt` is stamped — write-once, nothing clears the latch. Terminal. */
  | "revoked"
  /**
   * 404. The index row outlived the transcript it points at: `sessions_index`
   * and `app.session_history` share a retention horizon, but a row can still be
   * listed while its content is already swept. Terminal — there is nothing left
   * to open.
   */
  | "gone"
  /** Network, 5xx, or a schema mismatch. Retrying is honest here. */
  | "unavailable"
  /**
   * The row is listed as a chat, but the server derives an active flow for it:
   * the stored kind is stale (a promotion write lost on the conversation's last
   * turn, or a refine driven entirely through card frames), and reopening an
   * issue conversation is not offered here. Terminal — retrying resolves the
   * same flow forever, and the landing surface is the one this rail withholds.
   */
  | "issueOnly"
  /**
   * 401, and always the `no_session` dialect: the detail route runs
   * `requireSession` and nothing else, so a dead GitLab token cannot produce it.
   * `lib/http` fires the app's one sign-in navigation from the mint site, and
   * the rail paints its own panel besides — `beforeLoad` runs on a route MATCH
   * and a drawer click is not one, so a refusal that renders nothing is silent.
   */
  | "signedOut";

// `GET /sessions/:id`, as the shared model plus the two fields only the rail
// reads. `flowIssueIid` is `.optional()` so a client running against a server
// that predates it degrades to the chat surface instead of failing the parse.
const SessionResumeDetailSchema = SessionDetailSchema.extend({
  endedAt: z.string().nullable(),
  flowIssueIid: z.number().nullable().optional(),
});

export interface FocusSessionPick {
  readonly pick: (entry: SessionIndexEntry) => void;
  /** The row whose resolve is in flight — the rail disables the list on it. */
  readonly pendingSessionId: string | null;
  /** The last pick's refusal, or null. The rail owns how each kind reads. */
  readonly refusal: PickRefusal | null;
  /** The session id the last refusal was about — the rail mutes that row. */
  readonly refusedSessionId: string | null;
  /**
   * Drop a failed pick's notice. `refusal` outlives the drawer that shows it —
   * the rail unmounts on close while this machine lives on the host — so the
   * rail calls this from its own unmount cleanup, scoping the notice to the
   * opening of the drawer that produced it.
   */
  readonly clearRefusal: () => void;
  /**
   * Re-run the last pick. The retry is only offered on `unavailable`, and it
   * re-picks the SAME entry rather than reloading the list: the list is not
   * what failed.
   */
  readonly retry: () => void;
}

export const useFocusSessionPick = (
  projectId: number,
  /**
   * Whether the surface behind the drawer is the CHAT — i.e. the user is
   * standing in the chat slot. Every other Focus surface (the wizard stages, a
   * refine URL, an implement run) stands in the issue slot or in none, where the
   * bound chat is a conversation the user LEFT.
   */
  chatStageActive: boolean,
  onResolved: (target: PickTarget) => void,
): FocusSessionPick => {
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<PickRefusal | null>(null);
  const [refusedSessionId, setRefusedSessionId] = useState<string | null>(null);
  // The guard is the REF, not the state: state settles a commit later, so two
  // clicks inside one tick would both pass a state check and adopt twice — two
  // pipelines for one gesture. The state exists for rendering only.
  const pendingRef = useRef<string | null>(null);
  // The entry the last pick was for, so `retry` re-runs THAT pick rather than
  // reloading a list that never failed.
  const lastEntryRef = useRef<SessionIndexEntry | null>(null);

  const pick = useCallback(
    (entry: SessionIndexEntry): void => {
      // (1) GUARD. Re-picking the session already open is a no-op — no fetch,
      // no detach, no adopt — and a second row while one resolve is in flight
      // is ignored, so one gesture can start at most one adoption.
      //
      // "Already open" is the chat binding only while the surface behind the
      // drawer IS the chat — the SURFACE, not the URL: every wizard stage
      // (`intro`, `createTask`, `search`, …) sits on the same project URL a chat
      // does while standing in the issue slot, so a URL-shaped test would badge
      // the bound chat "open now" on all of them and swallow exactly the click
      // that goes back to it. The rail's badge reads the same flag.
      if (
        chatStageActive &&
        entry.sessionId === readProjectSessionId(projectId, "chat")
      ) {
        return;
      }
      // The two slots stay DISJOINT by construction, not only by the write path:
      // a row that is currently bound to issue work — the pre-warm, ANY issue's
      // own binding, or the older bundle's one binding — must not also become
      // the chat's, or one session would answer both and the next refine turn
      // would put an issue conversation inside the chat window. Every issue
      // binding is checked, not one: issue work binds per issue, so a row can
      // match #212's session while the pre-warm is another. Terminal, and the
      // same refusal a stale chat label gets — from the user's side it is the
      // same fact.
      if (isIssueWorkSession(projectId, entry.sessionId)) {
        lastEntryRef.current = entry;
        setRefusal("issueOnly");
        setRefusedSessionId(entry.sessionId);
        return;
      }
      if (pendingRef.current !== null) return;
      pendingRef.current = entry.sessionId;
      lastEntryRef.current = entry;
      setPendingSessionId(entry.sessionId);
      setRefusal(null);
      setRefusedSessionId(null);
      // (2) RESOLVE. The detail route is the only carrier of `endedAt` and the
      // derived `flowIssueIid`.
      void httpGet(`/sessions/${entry.sessionId}`, SessionResumeDetailSchema)
        .then((detail) => {
          // (3) REVOKED. Before any binding write and before any navigation:
          // `endedAt` is write-once and the session boots tool-less, so the
          // honest answer is to say so rather than to open it.
          if (detail.endedAt !== null) {
            setRefusal("revoked");
            setRefusedSessionId(entry.sessionId);
            return;
          }
          // (4) IMPLEMENT. A dedicated run — resumed by id, never adopted, or
          // it would hijack the project's warm PM binding for every later
          // refine.
          if (detail.implement) {
            onResolved({
              kind: "implement",
              iid: detail.implement.issueIid,
              title: detail.implement.issueTitle,
              webUrl: detail.implement.issueWebUrl,
              resumeSessionId: entry.sessionId,
            });
            return;
          }
          // (5) ISSUE-ONLY. Before any binding write, exactly like `revoked`:
          // the row reached this list as a chat, and a derived flow says it is
          // an issue conversation. Reopening one is withheld, so the honest
          // answer is a refusal rather than a landing.
          if (typeof detail.flowIssueIid === "number") {
            setRefusal("issueOnly");
            setRefusedSessionId(entry.sessionId);
            return;
          }
          // (6) DETACH the session being left, byte-identical to the pagehide
          // beacon. Last moment its id is knowable.
          const outgoing = readProjectSessionId(projectId, "chat");
          if (outgoing !== null && outgoing !== entry.sessionId) {
            detachSession(outgoing);
          }
          // (7) ADOPT into the chat slot — the binding this surface reads.
          adoptProjectSession(projectId, entry.sessionId, "chat");
          // (8) ROUTE. No derived flow: the last user turn was free-form, which
          // is the chat surface. `PickTarget.refine` is the landing this pass
          // withholds at the refusal above, not a branch that fires.
          onResolved({ kind: "chat" });
        })
        .catch((err: unknown) => {
          // Three different truths behind one rejection: a swept transcript
          // is permanent, a 401 is a lapsed Loom sign-in with its own recovery,
          // and only the rest is worth a "try again".
          setRefusal(
            err instanceof UnauthorizedError
              ? "signedOut"
              : err instanceof HttpError && err.status === 404
                ? "gone"
                : "unavailable",
          );
          setRefusedSessionId(entry.sessionId);
        })
        .finally(() => {
          pendingRef.current = null;
          setPendingSessionId(null);
        });
    },
    [projectId, chatStageActive, onResolved],
  );

  const clearRefusal = useCallback(() => {
    setRefusal(null);
    setRefusedSessionId(null);
  }, []);

  // The retry re-picks the entry itself, so the guard, the resolve order and
  // the revoked-before-any-write rule all run again exactly as they did.
  const retry = useCallback(() => {
    const entry = lastEntryRef.current;
    if (entry === null) return;
    setRefusal(null);
    setRefusedSessionId(null);
    pick(entry);
  }, [pick]);

  return {
    pick,
    pendingSessionId,
    refusal,
    refusedSessionId,
    clearRefusal,
    retry,
  };
};
